import React, { useState, useEffect } from 'react';
import { t } from '../utils/translations.js';

const PodcastHub = ({ ghostAPIUrl = '/ghost/api/content', apiKey = '' }) => {
  const [episodes, setEpisodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('all');

  useEffect(() => {
    fetchEpisodes();
  }, []);

  const resolveContentApiKey = () => {
    if (apiKey && apiKey !== 'your-content-api-key-here') {
      return apiKey;
    }

    const metaKey = document.querySelector('meta[name="ghost-content-api-key"]')?.content;
    if (metaKey) {
      return metaKey;
    }

    if (window.ghostContentAPIKey && window.ghostContentAPIKey !== 'your-content-api-key-here') {
      return window.ghostContentAPIKey;
    }

    return '';
  };

  const fetchEpisodes = async () => {
    try {
      const contentApiKey = resolveContentApiKey();
      if (!contentApiKey) {
        throw new Error('Ghost Content API key not configured');
      }

      const baseUrl = ghostAPIUrl.replace(/\/$/, '');
      const query = [
        `key=${encodeURIComponent(contentApiKey)}`,
        'filter=tag:podcast,tag:hash-podcast',
        'include=tags',
        'fields=id,title,slug,feature_image,excerpt,url,published_at,custom_excerpt',
        'order=published_at desc',
        'limit=50'
      ].join('&');

      const response = await fetch(`${baseUrl}/posts/?${query}`);
      if (!response.ok) {
        throw new Error(`Podcast fetch failed with status ${response.status}`);
      }

      const payload = await response.json();
      const posts = Array.isArray(payload.posts) ? payload.posts : [];

      const mappedEpisodes = posts.map((post) => ({
        id: post.id,
        title: post.title,
        publishedAt: post.published_at,
        featureImage: post.feature_image || 'https://picsum.photos/1200/600?random=podcast',
        excerpt: post.excerpt || post.custom_excerpt || '',
        url: post.url || `/podcasts/${post.slug}/`,
        duration: '--'
      }));

      setEpisodes(mappedEpisodes);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching episodes:', error);
      setEpisodes([]);
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const latestEpisode = episodes[0];
  const olderEpisodes = episodes.slice(1);

  return (
    <div className="podcast-hub-react">
      <style>{`
        .podcast-hub-react {
          font-family: var(--font-primary, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif);
        }

        .podcast-page-header {
          text-align: center;
          padding: clamp(4rem, 8vw, 6rem) 2rem clamp(3rem, 6vw, 4rem);
          background: linear-gradient(180deg, var(--surface-elevated, #ffffff) 0%, var(--color-bg-secondary, #f7f9fb) 100%);
          border-bottom: 1px solid var(--color-border-light, #edf1f5);
        }

        .podcast-page-title {
          font-family: var(--font-display, 'Inter', sans-serif);
          font-size: clamp(3rem, 6vw, 4.2rem);
          font-weight: 800;
          color: var(--color-text-primary, #111827);
          letter-spacing: -0.02em;
          margin: 0;
          background: linear-gradient(135deg, var(--color-text-primary, #111827) 0%, #334155 100%);
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
          opacity: 0;
          animation: fadeInUp 0.6s ease forwards;
        }

        .podcast-hero {
          position: relative;
          margin: 3rem auto;
          max-width: 1200px;
          padding: 0 2rem;
          opacity: 0;
          animation: fadeInUp 0.8s ease 0.2s forwards;
        }

        .podcast-hero-card {
          position: relative;
          display: block;
          background: var(--surface-elevated, #ffffff);
          border-radius: 18px;
          overflow: hidden;
          box-shadow: 0 18px 40px rgba(0,0,0,0.12);
          transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
          text-decoration: none;
          color: var(--color-text-primary, #111827);
        }

        .podcast-hero-card:hover {
          transform: translateY(-6px);
          box-shadow: 0 28px 56px rgba(0,0,0,0.14);
        }

        .podcast-hero-image {
          width: 100%;
          height: 400px;
          object-fit: cover;
          filter: brightness(0.82);
          transition: all 0.6s ease;
        }

        .podcast-hero-card:hover .podcast-hero-image {
          filter: brightness(0.9);
          transform: scale(1.03);
        }

        .podcast-hero-content {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          padding: 2.5rem;
          background: linear-gradient(180deg, rgba(255,255,255,0.92) 0%, rgba(255,255,255,0.75) 35%, rgba(0,0,0,0.18) 100%);
          color: var(--color-text-primary, #111827);
        }

        .podcast-hero-badge {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          background: rgba(17, 24, 39, 0.08);
          color: var(--color-text-primary, #111827);
          padding: 0.5rem 1rem;
          border-radius: 18px;
          font-size: 0.75rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          margin-bottom: 1rem;
          border: 1px solid var(--color-border, #e5e7eb);
        }

        .podcast-hero-title {
          font-size: clamp(1.9rem, 4vw, 2.8rem);
          font-weight: 800;
          line-height: 1.2;
          margin: 0;
          color: var(--color-text-primary, #111827);
          text-shadow: 0 1px 6px rgba(255,255,255,0.6);
        }

        .podcast-hero-meta {
          display: flex;
          align-items: center;
          gap: 1.5rem;
          margin-top: 1rem;
          font-size: 0.95rem;
          color: var(--color-text-secondary, #4b5563);
          font-weight: 600;
        }

        .podcast-subscribe-hub {
          text-align: center;
          padding: 3rem 2rem;
          background: var(--surface-elevated, #ffffff);
          border-radius: 16px;
          margin: 3rem auto;
          max-width: 1200px;
          opacity: 0;
          animation: fadeInUp 0.8s ease 0.4s forwards;
          box-shadow: 0 12px 28px rgba(0,0,0,0.06);
          border: 1px solid var(--color-border, #e5e7eb);
        }

        .podcast-subscribe-title {
          font-size: 0.875rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          color: var(--color-text-muted, #9ca3af);
          margin-bottom: 2rem;
        }

        .podcast-subscribe-links {
          display: flex;
          justify-content: center;
          flex-wrap: wrap;
          gap: 1rem;
        }

        .podcast-subscribe-link {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 1rem 2rem;
          background: white;
          border: 1px solid var(--color-border, #e5e7eb);
          border-radius: 12px;
          text-decoration: none;
          color: var(--color-text-primary, #111827);
          font-weight: 600;
          font-size: 0.95rem;
          transition: all 0.3s ease;
          box-shadow: 0 2px 4px rgba(0,0,0,0.05);
        }

        .podcast-subscribe-link:hover {
          background: var(--primary-color, #6d28d9);
          color: white;
          border-color: var(--primary-color, #6d28d9);
          transform: translateY(-2px);
          box-shadow: 0 8px 16px rgba(109, 40, 217, 0.3);
        }

        .podcast-feed-container {
          max-width: 900px;
          margin: 4rem auto;
          padding: 0 2rem;
        }

        .podcast-feed-title {
          font-size: clamp(2rem, 4vw, 2.4rem);
          font-weight: 700;
          color: var(--color-text-primary, #111827);
          margin-bottom: 2rem;
          padding-bottom: 1rem;
          border-bottom: 2px solid var(--color-border, #e5e7eb);
          opacity: 0;
          animation: fadeInUp 0.6s ease 0.6s forwards;
        }

        .episode-list {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }

        .episode-card {
          display: flex;
          gap: 1.5rem;
          padding: 1.5rem;
          border: 1px solid var(--color-border, #e5e7eb);
          border-radius: 12px;
          background: var(--surface-elevated, #ffffff);
          text-decoration: none;
          color: inherit;
          transition: all 0.3s ease;
          opacity: 0;
          animation: fadeInUp 0.6s ease forwards;
          box-shadow: 0 6px 18px rgba(0,0,0,0.05);
        }

        .episode-card:nth-child(1) { animation-delay: 0.7s; }
        .episode-card:nth-child(2) { animation-delay: 0.8s; }
        .episode-card:nth-child(3) { animation-delay: 0.9s; }

        .episode-card:hover {
          transform: translateX(6px);
          box-shadow: 0 12px 30px rgba(0,0,0,0.08);
          border-color: var(--primary-color, #6d28d9);
        }

        .episode-card-image {
          width: 120px;
          height: 120px;
          border-radius: 8px;
          object-fit: cover;
          flex-shrink: 0;
        }

        .episode-card-content {
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .episode-card-meta {
          display: flex;
          align-items: center;
          gap: 1rem;
          font-size: 0.875rem;
          color: var(--color-text-muted, #9ca3af);
          font-weight: 600;
        }

        .episode-card-title {
          font-size: 1.35rem;
          font-weight: 700;
          line-height: 1.3;
          color: var(--color-text-primary, #111827);
          margin: 0;
          transition: color 0.3s ease;
        }

        .episode-card:hover .episode-card-title {
          color: var(--primary-color, #6d28d9);
        }

        .episode-card-excerpt {
          color: var(--color-text-secondary, #6b7280);
          font-size: 0.95rem;
          line-height: 1.6;
          margin: 0;
        }

        /* Dark mode readability fix for podcasts page */
        [data-theme="dark"] .podcast-page-header {
          background: linear-gradient(180deg, #0b1220 0%, #0f172a 100%);
          border-bottom-color: rgba(148, 163, 184, 0.2);
        }

        [data-theme="dark"] .podcast-page-title {
          background: linear-gradient(135deg, #f8fafc 0%, #dbeafe 100%);
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
        }

        [data-theme="dark"] .podcast-hero-card,
        [data-theme="dark"] .podcast-subscribe-hub,
        [data-theme="dark"] .episode-card {
          background: #111827;
          border-color: rgba(148, 163, 184, 0.28);
          box-shadow: 0 12px 30px rgba(0, 0, 0, 0.42);
        }

        [data-theme="dark"] .podcast-hero-content {
          background: linear-gradient(180deg, rgba(15, 23, 42, 0.35) 0%, rgba(15, 23, 42, 0.78) 52%, rgba(2, 6, 23, 0.9) 100%);
          color: #f8fafc;
        }

        [data-theme="dark"] .podcast-hero-badge {
          background: rgba(30, 41, 59, 0.75);
          color: #e2e8f0;
          border-color: rgba(148, 163, 184, 0.4);
        }

        [data-theme="dark"] .podcast-hero-title,
        [data-theme="dark"] .podcast-feed-title,
        [data-theme="dark"] .episode-card-title,
        [data-theme="dark"] .podcast-subscribe-title {
          color: #f8fafc;
          text-shadow: none;
        }

        [data-theme="dark"] .podcast-hero-meta,
        [data-theme="dark"] .episode-card-meta,
        [data-theme="dark"] .episode-card-excerpt {
          color: #cbd5e1;
        }

        [data-theme="dark"] .podcast-feed-title {
          border-bottom-color: rgba(148, 163, 184, 0.35);
        }

        [data-theme="dark"] .podcast-subscribe-link {
          background: #0f172a;
          border-color: rgba(148, 163, 184, 0.35);
          color: #f8fafc;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
        }

        [data-theme="dark"] .podcast-subscribe-link:hover {
          background: #1e3a8a;
          border-color: #60a5fa;
          color: #ffffff;
        }

        .loading-spinner {
          display: flex;
          justify-content: center;
          align-items: center;
          padding: 4rem;
        }

        .spinner {
          width: 50px;
          height: 50px;
          border: 4px solid var(--color-border-light, #f3f4f6);
          border-top-color: var(--primary-color, #6d28d9);
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
        }

        @keyframes fadeInUp {
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        @media (max-width: 768px) {
          .podcast-hero,
          .podcast-subscribe-hub,
          .podcast-feed-container {
            margin-left: 1rem;
            margin-right: 1rem;
          }

          .podcast-hero-content {
            padding: 2rem 1.5rem;
          }

          .episode-card {
            flex-direction: column;
          }

          .episode-card-image {
            width: 100%;
            height: 200px;
          }

          .podcast-subscribe-links {
            flex-direction: column;
          }

          .podcast-subscribe-link {
            width: 100%;
            max-width: 280px;
          }
        }
      `}</style>

      <header className="podcast-page-header">
        <h1 className="podcast-page-title">{t('podcasts.title', 'Podcast')}</h1>
      </header>

      {loading ? (
        <div className="loading-spinner">
          <div className="spinner"></div>
        </div>
      ) : (
        <>
          {latestEpisode && (
            <section className="podcast-hero">
              <a href={latestEpisode.url} className="podcast-hero-card">
                <img 
                  src={latestEpisode.featureImage} 
                  alt={latestEpisode.title}
                  className="podcast-hero-image"
                />
                <div className="podcast-hero-content">
                  <span className="podcast-hero-badge">
                    🔴 {t('podcasts_page.latest_episode', 'Latest Episode')}
                  </span>
                  <h2 className="podcast-hero-title">{latestEpisode.title}</h2>
                  <div className="podcast-hero-meta">
                    <span>{formatDate(latestEpisode.publishedAt)}</span>
                    <span>•</span>
                    <span>{latestEpisode.duration}</span>
                  </div>
                </div>
              </a>
            </section>
          )}

          <section className="podcast-subscribe-hub">
            <h2 className="podcast-subscribe-title">{t('podcasts_page.listen_subscribe_on', 'Listen & Subscribe On')}</h2>
            <div className="podcast-subscribe-links">
              <a href="#" className="podcast-subscribe-link">
                🎵 {t('podcasts.spotify', 'Spotify')}
              </a>
              <a href="#" className="podcast-subscribe-link">
                ▶️ {t('podcasts_page.youtube', 'YouTube')}
              </a>
              <a href="#" className="podcast-subscribe-link">
                🎧 {t('podcasts.apple', 'Apple Podcasts')}
              </a>
            </div>
          </section>

          {olderEpisodes.length > 0 && (
            <section className="podcast-feed-container">
              <h2 className="podcast-feed-title">{t('podcasts_page.all_episodes', 'All Episodes')}</h2>
              <div className="episode-list">
                {olderEpisodes.map((episode) => (
                  <a key={episode.id} href={episode.url} className="episode-card">
                    <img 
                      src={episode.featureImage} 
                      alt={episode.title}
                      className="episode-card-image"
                    />
                    <div className="episode-card-content">
                      <div className="episode-card-meta">
                        <time>{formatDate(episode.publishedAt)}</time>
                        <span>•</span>
                        <span>{episode.duration}</span>
                      </div>
                      <h3 className="episode-card-title">{episode.title}</h3>
                      <p className="episode-card-excerpt">{episode.excerpt}</p>
                    </div>
                  </a>
                ))}
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
};

export default PodcastHub;
